import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBolt, faCheckCircle, faFilePdf, faEye, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api';
import './AutomatedEvaluation.css';

// Rendered as a section of the Upload Question Papers page, not as a route of
// its own. Automated Evaluation and Upload Files always had the same audience -
// Data Operator and Admin, nobody else - so splitting them across two pages made
// a user navigate between two halves of one job. Its own page header and its
// "Back to Upload Question Papers" button are gone; the tab strip on the Upload
// page does that now.
//
// How many pending slots one page shows. The backend accepts up to 50; ten keeps
// the list readable and, with the pager below it, no longer caps what is
// reachable - which is what it used to do.
const PENDING_PAGE_SIZE = 10;

// Above this many papers, one click is a big enough commitment to be worth
// confirming - at the measured rate a few hundred papers is most of a day of LLM
// work. It no longer caps what can be queued: the operator queues everything
// their filters match, and the backend is what cuts that into batches (200 by
// default, PREEVAL_QUEUE_BATCH_SIZE). This is only the point at which the button
// asks whether you meant it.
const BULK_CONFIRM_ABOVE = 200;

// Pure functions of their own arguments only - no component state/props - so
// they live at module scope rather than being recreated every render (which
// is also what let react-hooks/exhaustive-deps flag normalizeCourseRecord as
// a "missing dependency" of the mount-only course-fetch effect below; hoisting
// it out here removes the warning instead of suppressing it).
const normalizeText = (value) => (value || '').toString().trim().replace(/\s+/g, ' ');

const getCourseKey = (courseCode, courseName) => `${normalizeText(courseCode).toLowerCase()}||${normalizeText(courseName).toLowerCase()}`;

const getExamTypeLabel = (examType) => {
  if (typeof examType === 'string') {
    return normalizeText(examType);
  }

  if (examType && typeof examType === 'object') {
    return normalizeText(examType.exam_type || examType.examType || examType.name || examType.label || '');
  }

  return '';
};

const getEvaluationTypeLabel = (examType, fallbackValue = '') => {
  if (examType && typeof examType === 'object') {
    return normalizeText(examType.evaluation_type || examType.evaluationType || fallbackValue || '');
  }

  return normalizeText(fallbackValue || '');
};

const parseSemesterName = (semesterName) => {
  const normalizedSemester = normalizeText(semesterName);
  if (!normalizedSemester) {
    return { academicYear: '', semester: '' };
  }

  const fullMatch = normalizedSemester.match(/^(\d{4})-(\d{4})\s+SEM\s+(\d+)$/i);
  if (fullMatch) {
    return {
      academicYear: `${fullMatch[1]}-${fullMatch[2].slice(-2)}`,
      semester: `Semester ${fullMatch[3]}`
    };
  }

  const shortMatch = normalizedSemester.match(/^(\d{4})-(\d{2})\s+SEM\s+(\d+)$/i);
  if (shortMatch) {
    return {
      academicYear: `${shortMatch[1]}-${shortMatch[2]}`,
      semester: `Semester ${shortMatch[3]}`
    };
  }

  return { academicYear: '', semester: '' };
};

const normalizeCourseRecord = (course) => {
  const courseCode = normalizeText(course.course_code || course.course_id || course.courseCode || '');
  const courseName = normalizeText(course.course_name || course.course_title || course.courseName || '');
  const batchName = normalizeText(course.batch_name || course.batch || '');
  const semesterName = normalizeText(course.semester_name || course.semester || '');
  const examTypeValue = course.exam_type || course.examType || '';
  const facultyEmailId = normalizeText(course.faculty_email_id || course.facultyEmailId || course.instructor_email || '');

  return {
    course_code: courseCode,
    course_name: courseName,
    batch_name: batchName,
    semester_name: semesterName,
    faculty_email_id: facultyEmailId,
    exam_type: getExamTypeLabel(examTypeValue),
    evaluation_type: getEvaluationTypeLabel(examTypeValue, course.evaluation_type || course.evaluationType || ''),
    course_key: getCourseKey(courseCode, courseName)
  };
};

const AutomatedEvaluation = () => {
  const [availableCourses, setAvailableCourses] = useState([]);
  const [courseRecords, setCourseRecords] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);
  const [showCourseNameDropdown, setShowCourseNameDropdown] = useState(false);
  const [showCourseIdDropdown, setShowCourseIdDropdown] = useState(false);
  const [courseNameSearch, setCourseNameSearch] = useState('');
  const [courseIdSearch, setCourseIdSearch] = useState('');
  const [selectedCourseKey, setSelectedCourseKey] = useState('');
  const [evaluationDetails, setEvaluationDetails] = useState({
    examType: '',
    examFormat: '',
    academicYear: '',
    semester: '',
    programCommittee: '',
    batch: '',
    evaluationType: '',
    instructorName: '',
    courseId: '',
    courseName: ''
  });
  const [userData, setUserData] = useState({});
  const [academicYearError, setAcademicYearError] = useState('');

  // The one remaining action is a lookup, not a trigger. The background
  // pre-evaluation job has already fetched, partitioned and scored papers across
  // the QPM catalogue, so this reads results instead of processing anything -
  // which is why it answers in milliseconds where live processing takes ~45s per
  // paper. The only processing this page can still start is the per-slot
  // "Evaluate now" below, which is deliberately one paper at a time.
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  // Per-slot progress for "Evaluate now", keyed by slot rather than held as a
  // single flag: several pending rows can be in flight at once and each needs
  // its own state.
  const [slotState, setSlotState] = useState({});
  // Which page of the pending list is showing. The pending side is enumerated
  // from the upstream catalogue and routinely runs to thousands of slots, so it
  // is paged rather than truncated - narrowing the filters used to be the only
  // way to see past the first ten.
  const [pendingOffset, setPendingOffset] = useState(0);

  // Bulk queueing. `bulkQueue` holds the outcome of the last click so the
  // operator gets a durable answer ("N queued") rather than a toast they might
  // miss - queuing 200 papers is a commitment worth leaving on screen.
  const [isQueueing, setIsQueueing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [bulkQueue, setBulkQueue] = useState(null);
  // Preview of one already-evaluated paper. Read straight out of storage: the
  // paper is stored, so there is nothing to fetch from upstream.
  const [previewPaperId, setPreviewPaperId] = useState('');
  // What is typed into the picker's filter box. Separate from the page's own
  // metadata form: this narrows the list of already-matched papers, it does not
  // re-run the search.
  const [previewQuery, setPreviewQuery] = useState('');
  const [paperPreview, setPaperPreview] = useState({ url: '', loading: false, error: '' });
  // Preview of one not-yet-evaluated slot. This one does go upstream, but only
  // to read: see /upload/pre-evaluation/slot-preview. Deliberately one at a
  // time and only on request - a pending page is ten slots, and fetching ten
  // papers to render a list where at most one gets opened is waste.
  const [slotPreview, setSlotPreview] = useState({ key: '', url: '', loading: false, error: '', label: '' });

  const academicYearPattern = /^\d{4}-\d{2}$/;

  // What the preview picker chooses between. Evaluated papers only: a stored
  // paper with no completed analysis has a document but nothing to review, and
  // listing it beside the others would imply it does.
  const evaluatedResults = useMemo(
    () => (searchResults?.results || []).filter((paper) => paper.evaluated),
    [searchResults]
  );

  // The subset the picker shows. Course code or course name, because those are
  // the two things anyone knows a paper by - matching the whole formatted label
  // would also match exam types and years, which makes typing "EC3" select
  // everything.
  const visiblePreviewOptions = useMemo(() => {
    const query = normalizeText(previewQuery).toLowerCase();
    if (!query) return evaluatedResults;
    return evaluatedResults.filter((paper) =>
      `${paper.course_id || ''} ${paper.course_name || ''}`.toLowerCase().includes(query));
  }, [evaluatedResults, previewQuery]);

  const selectedCourseRecords = useMemo(
    () => courseRecords.filter((record) => record.course_key === selectedCourseKey),
    [courseRecords, selectedCourseKey]
  );

  const availableExamTypes = useMemo(() => {
    const seenExamTypes = new Set();

    return selectedCourseRecords
      .filter((record) => record.exam_type)
      .filter((record) => {
        const key = record.exam_type.toLowerCase();
        if (seenExamTypes.has(key)) {
          return false;
        }
        seenExamTypes.add(key);
        return true;
      })
      .map((record) => ({
        value: record.exam_type,
        label: record.exam_type,
        description: [record.batch_name, record.semester_name, record.evaluation_type || ''].filter(Boolean).join(' • '),
        record
      }));
  }, [selectedCourseRecords]);

  const selectedInstructorName = useMemo(() => {
    const matchedRecord = selectedCourseRecords.find((record) => record.faculty_email_id);
    return matchedRecord?.faculty_email_id || '';
  }, [selectedCourseRecords]);

  const extractErrorMessage = (error) => {
    if (typeof error === 'string') {
      // Remove any JSON wrapping first
      let cleanError = error;
      if (cleanError.includes('{') && cleanError.includes('}')) {
        try {
          const parsed = JSON.parse(cleanError);
          cleanError = parsed.detail || parsed.message || cleanError;
        } catch {
          // If parsing fails, remove JSON-like content
          cleanError = cleanError.replace(/\{[^}]*\}/g, '').trim();
        }
      }
      return cleanError || error;
    }
    return String(error);
  };


  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoadingUserData(true);
      try {
        const response = await fetch('/qpra/get-saml-data', {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            window.location.href = '/qprs/login';
            return;
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error('Error fetching SAML user data:', error);
      } finally {
        setIsLoadingUserData(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoadingCourses(true);
      try {
        const url = getApiUrl(SERVERS.UPLOAD, ENDPOINTS.UPLOAD_COURSE_DATA);
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch courses');
        }

        const data = await response.json();
        const normalizedRecords = (data.courses || [])
          .map(normalizeCourseRecord)
          .filter((course) => course.course_code && course.course_name);

        const uniqueCourses = [];
        const seenCourses = new Set();

        normalizedRecords.forEach((course) => {
          if (seenCourses.has(course.course_key)) {
            return;
          }
          seenCourses.add(course.course_key);
          uniqueCourses.push({
            course_code: course.course_code,
            course_name: course.course_name,
            course_key: course.course_key
          });
        });

        setCourseRecords(normalizedRecords);
        setAvailableCourses(uniqueCourses);
      } catch (error) {
        console.error('Error fetching courses for automated evaluation:', error);
        setCourseRecords([]);
        setAvailableCourses([]);
      } finally {
        setIsLoadingCourses(false);
      }
    };

    fetchCourses();
  }, []);

  const handleDetailsChange = (event) => {
    const { name, value } = event.target;

    if (name === 'academicYear') {
      if (value === '' || academicYearPattern.test(value)) {
        setAcademicYearError('');
      } else {
        setAcademicYearError('Academic Year must be in YYYY-YY format, for example 2024-25.');
      }
    }

    setEvaluationDetails((prev) => {
      const next = {
        ...prev,
        [name]: value
      };

      if (name === 'examType') {
        const matchedExamType = selectedCourseRecords.find(
          (record) => record.exam_type.toLowerCase() === value.toLowerCase()
        );

        if (matchedExamType) {
          next.batch = matchedExamType.batch_name;
          next.evaluationType = matchedExamType.evaluation_type;
          next.instructorName = matchedExamType.faculty_email_id || '';

          const semesterDetails = parseSemesterName(matchedExamType.semester_name);
          if (semesterDetails.academicYear) {
            next.academicYear = semesterDetails.academicYear;
          }
          if (semesterDetails.semester) {
            next.semester = semesterDetails.semester;
          }
        }
      }

      if (name === 'courseId') {
        const matchedCourse = availableCourses.find(
          (course) => course.course_code.toLowerCase() === value.toLowerCase()
        );

        if (matchedCourse) {
          next.courseName = matchedCourse.course_name;
          next.instructorName = selectedInstructorName;
        }
      }

      if (name === 'courseName') {
        const matchedCourse = availableCourses.find(
          (course) => course.course_name.toLowerCase() === value.toLowerCase()
        );

        if (matchedCourse) {
          next.courseId = matchedCourse.course_code;
          next.instructorName = selectedInstructorName;
        }
      }

      return next;
    });
  };

  const handleAcademicYearBlur = () => {
    if (!evaluationDetails.academicYear) {
      setAcademicYearError('Academic Year is required.');
      return;
    }

    if (!academicYearPattern.test(evaluationDetails.academicYear)) {
      setAcademicYearError('Academic Year must be in YYYY-YY format, for example 2024-25.');
      return;
    }

    setAcademicYearError('');
  };

  const handleCourseNameSearchChange = (event) => {
    const { value } = event.target;
    setCourseNameSearch(value);
    setShowCourseNameDropdown(true);
    setSelectedCourseKey('');
    setEvaluationDetails((prev) => ({
      ...prev,
      courseName: '',
      courseId: '',
      examType: '',
      batch: '',
      evaluationType: '',
      academicYear: '',
      semester: ''
    }));
  };

  const handleCourseIdSearchChange = (event) => {
    const { value } = event.target;
    setCourseIdSearch(value);
    setShowCourseIdDropdown(true);
    setSelectedCourseKey('');
    setEvaluationDetails((prev) => ({
      ...prev,
      courseName: '',
      courseId: '',
      examType: '',
      batch: '',
      evaluationType: '',
      academicYear: '',
      semester: ''
    }));
  };

  const normalizedCourseNameQuery = courseNameSearch.trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedCourseIdQuery = courseIdSearch.trim().replace(/\s+/g, ' ').toLowerCase();

  const filteredCoursesByName = useMemo(
    () => availableCourses.filter((course) =>
      course.course_name.toLowerCase().includes(normalizedCourseNameQuery)
    ),
    [availableCourses, normalizedCourseNameQuery]
  );

  const filteredCoursesById = useMemo(
    () => availableCourses.filter((course) =>
      course.course_code.toLowerCase().includes(normalizedCourseIdQuery)
    ),
    [availableCourses, normalizedCourseIdQuery]
  );

  const handleCourseSelect = (course) => {
    const matchedInstructorName = courseRecords.find((record) => record.course_key === course.course_key && record.faculty_email_id)?.faculty_email_id || '';

    setCourseNameSearch(course.course_name);
    setCourseIdSearch(course.course_code);
    setSelectedCourseKey(course.course_key);
    setEvaluationDetails((prev) => ({
      ...prev,
      courseName: course.course_name,
      courseId: course.course_code,
      examType: '',
      batch: '',
      evaluationType: '',
      instructorName: matchedInstructorName,
      academicYear: '',
      semester: ''
    }));
    setShowCourseNameDropdown(false);
    setShowCourseIdDropdown(false);
  };

  // Every field is optional: the point of this view is answering a query that
  // names as little as one field.
  // `offset` is which page of the pending list to ask for. Passed explicitly
  // rather than read from state because the callers disagree about it: a fresh
  // fetch or a filter change starts at the first page, the pager asks for a
  // specific one, and the refresh after an "Evaluate now" stays where the user
  // was rather than throwing them back to page one.
  const handleSearchEvaluated = async (offset = 0) => {
    setSearchError('');
    setSearchResults(null);
    setPendingOffset(offset);
    setPreviewQuery('');
    clearPaperPreview();
    clearSlotPreview();

    const params = new URLSearchParams();
    const mapping = {
      academic_year: evaluationDetails.academicYear,
      // The form shows "Semester 1"; question_papers stores "1".
      semester: (evaluationDetails.semester.match(/(\d+)/) || [])[1] || '',
      course_id: evaluationDetails.courseId,
      course_name: evaluationDetails.courseName,
      batch: evaluationDetails.batch,
      exam_type: evaluationDetails.examType,
      // Exam Format and Program Committee are intentionally not sent. QPM does
      // not supply either, so pre-evaluated papers store them empty and
      // filtering on them would always come back empty-handed. The inputs stay
      // on the form because "Evaluate now" passes them through when it creates
      // the paper for a slot the job has not reached.
      instructor_name: evaluationDetails.instructorName
    };
    Object.entries(mapping).forEach(([key, value]) => {
      if (value && String(value).trim()) params.append(key, String(value).trim());
    });

    setIsSearching(true);
    try {
      params.append('pending_offset', String(offset));
      params.append('pending_limit', String(PENDING_PAGE_SIZE));

      const response = await fetch(`/upload/pre-evaluation/search?${params.toString()}`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(body.detail || body.message || `HTTP ${response.status}`));
      }
      setSearchResults(await response.json());
    } catch (error) {
      setSearchError(extractErrorMessage(error.message || 'Failed to load evaluated papers'));
      console.error('Evaluated-paper search failed:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // --- previews ------------------------------------------------------------
  // Both previews render the actual document, which is the thing a professor is
  // deciding about; a count and a score do not tell you whether the paper in
  // front of you is the one you meant. They are fetched as blobs rather than
  // pointed at with a bare URL so the browser renders them inline - the stored
  // download endpoint sends Content-Disposition: attachment, which otherwise
  // makes it offer to save the file instead.
  const clearPaperPreview = () => {
    setPaperPreview((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { url: '', loading: false, error: '' };
    });
    setPreviewPaperId('');
  };

  const clearSlotPreview = () => {
    setSlotPreview((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { key: '', url: '', loading: false, error: '', label: '' };
    });
  };

  const loadPaperPreview = async (paperId) => {
    if (!paperId) return;
    setPreviewPaperId(paperId);
    setPaperPreview((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { url: '', loading: true, error: '' };
    });
    try {
      const url = `${getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPER)}/${paperId}/download`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Could not load that paper (HTTP ${response.status})`);
      const blob = await response.blob();
      setPaperPreview({ url: URL.createObjectURL(blob), loading: false, error: '' });
    } catch (error) {
      setPaperPreview({ url: '', loading: false, error: extractErrorMessage(error.message || 'Could not load that paper') });
    }
  };

  // A pending slot has no stored paper, so this goes to QPM - but only to read
  // it. The endpoint creates no paper row, writes nothing to MinIO, starts no
  // analysis and neither takes nor waits on the slot claim, so it cannot
  // interfere with an "Evaluate now" or a batch run on the same slot. It is a
  // separate endpoint from /evaluate-slot precisely so that is true by
  // construction rather than by care.
  const loadSlotPreview = async (slot) => {
    const key = slotKey(slot);
    const label = `${slot.course_id} · ${slot.exam_type} · ${slot.academic_year} SEM ${slot.semester}`;
    setSlotPreview((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { key, url: '', loading: true, error: '', label };
    });
    try {
      const params = new URLSearchParams({
        course_id: slot.course_id,
        batch: slot.batch,
        exam_type: slot.exam_type,
        academic_year: slot.academic_year,
        semester: slot.semester
      });
      // The slot's own evaluation_type, for the same reason "Evaluate now"
      // forwards it: QPM 404s an otherwise-identical request without it.
      if (slot.evaluation_type) params.append('evaluation_type', slot.evaluation_type);

      const response = await fetch(`/upload/pre-evaluation/slot-preview?${params.toString()}`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(body.detail || body.message || `HTTP ${response.status}`));
      }
      const blob = await response.blob();
      setSlotPreview({ key, url: URL.createObjectURL(blob), loading: false, error: '', label });
    } catch (error) {
      setSlotPreview({ key, url: '', loading: false, error: extractErrorMessage(error.message || 'Could not load that paper'), label });
    }
  };

  // The pending-slot preview panel. Rendered directly beneath the row whose
  // Preview was clicked rather than after the list: at the bottom of the page it
  // sat up to nine rows away from the click that produced it, with nothing
  // tying the two together.
  const renderSlotPreview = () => (
    <div className="ae-preview ae-preview-pending">
      <div className="ae-preview-head">
        <h3>
          <FontAwesomeIcon icon={faEye} /> {slotPreview.label}
        </h3>
        <button type="button" className="btn-preview-close" onClick={clearSlotPreview}>
          Close
        </button>
      </div>
      <p className="ae-preview-note">
        View only - this paper has not been stored or scored. Use{' '}
        <strong>Evaluate now</strong> on this row to do that.
      </p>
      {slotPreview.loading && (
        <p className="ae-preview-status">
          <FontAwesomeIcon icon={faSpinner} spin /> Fetching it from QPM...
        </p>
      )}
      {slotPreview.error && (
        <p className="ae-preview-status ae-preview-status-bad">{slotPreview.error}</p>
      )}
      {slotPreview.url && (
        <div className="ae-preview-frame">
          <object data={slotPreview.url} type="application/pdf">
            <p>
              This browser cannot display the PDF inline.{' '}
              <a href={slotPreview.url} target="_blank" rel="noreferrer">
                Open it in a new tab
              </a>
              .
            </p>
          </object>
        </div>
      )}
    </div>
  );

  // Open the first match automatically once a search comes back: with one result
  // there is nothing to choose, and with several the first is as good a starting
  // point as an empty frame.
  useEffect(() => {
    const first = (searchResults?.results || []).find((paper) => paper.evaluated);
    if (first) loadPaperPreview(first.paper_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  // Object URLs outlive the component unless revoked.
  useEffect(() => () => {
    if (paperPreview.url) URL.revokeObjectURL(paperPreview.url);
    if (slotPreview.url) URL.revokeObjectURL(slotPreview.url);
  }, [paperPreview.url, slotPreview.url]);

  // Queue every pending slot matching the current filters, in one request.
  //
  // Deliberately does not wait for the papers to be evaluated: at a couple of
  // minutes of LLM work each, a hundred of them is hours, and holding the
  // browser open for that is exactly the problem this replaces. The backend
  // creates ordinary evaluation runs and returns as soon as they are started,
  // so the answer here is "how many were accepted", not "how many are done".
  //
  // Re-runs the search afterwards so the evaluated and pending counts reflect
  // whatever finished while the request was in flight. Later progress is picked
  // up by pressing Fetch again - the counts are a snapshot, not a live feed.
  //
  // One click covers the whole filtered set, however large. The backend splits
  // it into batches and works them in order, so there is nothing to come back
  // and re-click.
  // Stops the batches this click started. Only offered while the response that
  // carried the group id is still on screen: a reload loses it, and the honest
  // thing is to stop showing a button that can no longer act rather than to
  // guess which group was meant.
  const handleCancelQueue = async () => {
    const group = bulkQueue?.queue_group_id;
    if (!group) return;
    if (!window.confirm(
      'Stop the remaining batches?\n\n'
      + 'Papers already being evaluated will finish - stopping one mid-paper '
      + 'would leave it stored with no score. Everything not yet started is '
      + 'dropped, and stays pending for a later run.')) return;
    setIsCancelling(true);
    try {
      const response = await fetch(
        `/upload/pre-evaluation/queue-groups/${group}/cancel`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setBulkQueue((prev) => ({ ...prev, cancelError: data.detail
          || `Cancel failed (HTTP ${response.status}).` }));
        return;
      }
      setBulkQueue((prev) => ({ ...prev, cancelled: data }));
      handleSearchEvaluated(pendingOffset);
    } catch (error) {
      setBulkQueue((prev) => ({ ...prev, cancelError: `Cancel failed: ${error.message}` }));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleQueueAllPending = async () => {
    const total = searchResults?.pending?.count || 0;
    if (!total) return;
    // The only guard on size. Everything past here is committed.
    if (total > BULK_CONFIRM_ABOVE && !window.confirm(
      `Queue all ${total} pending papers?\n\n`
      + `They are evaluated in the background in batches, a few minutes each, so `
      + `this is a long job. You can close the page - it keeps going.`)) return;
    setIsQueueing(true);
    setBulkQueue(null);
    try {
      // No max_items: the backend takes everything matching these filters and
      // decides its own batch size.
      const body = {};
      Object.entries(searchResults?.filters_applied || {}).forEach(([key, value]) => {
        if (value && String(value).trim()) body[key] = String(value).trim();
      });
      const response = await fetch('/upload/pre-evaluation/queue-pending', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setBulkQueue({ error: data.detail || `Queueing failed (HTTP ${response.status}).` });
        return;
      }
      setBulkQueue(data);
      handleSearchEvaluated(pendingOffset);
    } catch (error) {
      setBulkQueue({ error: `Queueing failed: ${error.message}` });
    } finally {
      setIsQueueing(false);
    }
  };

  // --- per-slot "Evaluate now" ---------------------------------------------
  // Narrow on purpose: one named slot per click, taken from the pending list of
  // the search above. Not an upload button - there is no file and no form-wide
  // trigger. The backend creates a one-item EvaluationRun so this is tracked
  // exactly like any batch run, and claims the slot so it cannot collide with a
  // batch run working the same period.
  // batch is part of the key, not just the label: the same course/exam_type/
  // period commonly has several pending slots that differ only by batch (e.g.
  // "KP65_1-2024" vs "CM55_2-2024" vs "AIML (AA05 & AB05_1-2024)"). Without it,
  // every such row shared one React key and one slotState entry, so evaluating
  // one batch made every other batch's row for that course silently inherit
  // its spinner/done/error state - a row could sit on "Evaluating..." forever
  // because it was actually reading a DIFFERENT batch's poll, not its own.
  const slotKey = (slot) =>
    `${slot.course_id}|${slot.exam_type}|${slot.academic_year}|${slot.semester}|${slot.batch}`;

  const handleEvaluateSlot = async (slot) => {
    const key = slotKey(slot);
    setSlotState((prev) => ({ ...prev, [key]: { phase: 'starting' } }));

    try {
      const response = await fetch('/upload/pre-evaluation/evaluate-slot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: slot.course_id,
          course_name: slot.course_name,
          exam_type: slot.exam_type,
          batch: slot.batch,
          academic_year: slot.academic_year,
          semester: slot.semester,
          instructor_name: slot.instructor_name || evaluationDetails.instructorName || '',
          // The slot's own evaluation_type, not the form's. QPM returns 404 for
          // an otherwise-identical request that omits it, so this is load-bearing
          // rather than decorative - and the slot came from the upstream
          // catalogue, which is where the authoritative value is.
          evaluation_type: slot.evaluation_type || evaluationDetails.evaluationType || ''
        })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        // 409 is the slot already being evaluated, or already done - a real
        // answer rather than a failure, so it is worth showing verbatim.
        throw new Error(extractErrorMessage(body.detail || body.message || `HTTP ${response.status}`));
      }
      setSlotState((prev) => ({
        ...prev,
        [key]: { phase: 'running', runId: body.run_id, itemId: body.item_id }
      }));
      pollSlotRun(key, body.run_id);
    } catch (error) {
      setSlotState((prev) => ({
        ...prev,
        [key]: { phase: 'error', message: error.message || 'Could not start evaluation' }
      }));
    }
  };

  // Poll the ordinary run-status endpoint. One slot is ~45s of work, so this is
  // a short wait rather than something to walk away from.
  const pollSlotRun = async (key, runId) => {
    const deadline = Date.now() + 10 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 4000));
      try {
        const response = await fetch(`/upload/pre-evaluation/runs/${runId}`);
        if (!response.ok) continue;
        const summary = await response.json();
        if (!['completed', 'failed'].includes(summary.status)) continue;

        const categories = summary.by_category || {};
        const done = (summary.by_status || {}).completed > 0;
        setSlotState((prev) => ({
          ...prev,
          [key]: done
            ? { phase: 'done', questions: summary.questions_extracted }
            : {
                phase: 'error',
                message: Object.keys(categories).length
                  ? `Did not complete: ${Object.entries(categories).map(([c, n]) => `${c} (${n})`).join(', ')}`
                  : 'Did not complete.'
              }
        }));
        // Refresh so a finished slot moves from pending to evaluated. Same page
        // of the pending list, not the first - the user was reading this one.
        if (done) handleSearchEvaluated(pendingOffset);
        return;
      } catch {
        // Transient - keep polling until the deadline.
      }
    }
    setSlotState((prev) => ({
      ...prev,
      [key]: { phase: 'error', message: 'Still running after 10 minutes. Check the run status.' }
    }));
  };

  return (
    <div className="ae-embedded">
      <div className="ae-embedded-inner">

        <div className="automated-evaluation-card evaluation-details-card">
          <div className="evaluation-details-header">
            <h2>Find Evaluated Papers</h2>
            {/* Every field is a filter, and every one is optional - the
                asterisks that used to be here belonged to the live
                fetch-and-upload actions, which validated the whole form
                before calling upstream. Nothing on this page requires a
                complete form any more. */}
            <p>
              Narrow the search with any combination of these - or none, to see
              everything already evaluated.
            </p>
          </div>

          {isLoadingCourses && (
            <div className="loading-state-message">
              <div className="loading-spinner"></div>
              <p>Loading course data...</p>
            </div>
          )}

          {isLoadingUserData && (
            <div className="loading-state-message">
              <div className="loading-spinner"></div>
              <p>Loading user data...</p>
            </div>
          )}

          <div className="evaluation-details-grid">
            <div className="evaluation-form-group">
              <label htmlFor="courseName">Course Name</label>
              <div className="search-dropdown-container">
                <input
                  id="courseName"
                  name="courseName"
                  type="text"
                  value={courseNameSearch}
                  onChange={handleCourseNameSearchChange}
                  onInput={handleCourseNameSearchChange}
                  onFocus={() => setShowCourseNameDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCourseNameDropdown(false), 150)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="evaluation-input"
                  placeholder="Search course name"
                  required
                />
                {showCourseNameDropdown && (
                  <div className="search-dropdown-list" key={`name-${normalizedCourseNameQuery}`}>
                    {filteredCoursesByName.length > 0 ? (
                      filteredCoursesByName.map((course) => (
                        <div
                          key={`name-${course.course_code}-${course.course_name}`}
                          className="search-dropdown-item"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleCourseSelect(course);
                          }}
                        >
                          <div className="dropdown-primary">{course.course_name}</div>
                          <div className="dropdown-secondary">{course.course_code}</div>
                        </div>
                      ))
                    ) : (
                      <div className="search-dropdown-empty">No matching courses</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="courseId">Course ID</label>
              <div className="search-dropdown-container">
                <input
                  id="courseId"
                  name="courseId"
                  type="text"
                  value={courseIdSearch}
                  onChange={handleCourseIdSearchChange}
                  onInput={handleCourseIdSearchChange}
                  onFocus={() => setShowCourseIdDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCourseIdDropdown(false), 150)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="evaluation-input"
                  placeholder="Search course ID"
                  required
                />
                {showCourseIdDropdown && (
                  <div className="search-dropdown-list" key={`id-${normalizedCourseIdQuery}`}>
                    {filteredCoursesById.length > 0 ? (
                      filteredCoursesById.map((course) => (
                        <div
                          key={`id-${course.course_code}-${course.course_name}`}
                          className="search-dropdown-item"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleCourseSelect(course);
                          }}
                        >
                          <div className="dropdown-primary">{course.course_code}</div>
                          <div className="dropdown-secondary">{course.course_name}</div>
                        </div>
                      ))
                    ) : (
                      <div className="search-dropdown-empty">No matching courses</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="academicYear">Academic Year</label>
              <input
                id="academicYear"
                name="academicYear"
                type="text"
                value={evaluationDetails.academicYear}
                onChange={handleDetailsChange}
                onBlur={handleAcademicYearBlur}
                className={`evaluation-input ${academicYearError ? 'input-error' : ''}`}
                placeholder="YYYY-YY"
                pattern="\d{4}-\d{2}"
                title="Use the format YYYY-YY, for example 2024-25"
                maxLength={7}
                required
              />
              <span className={`field-hint ${academicYearError ? 'field-error' : ''}`}>
                {academicYearError || 'Use the exact format like 2024-25.'}
              </span>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="semester">Semester</label>
              <select
                id="semester"
                name="semester"
                value={evaluationDetails.semester}
                onChange={handleDetailsChange}
                className="evaluation-input"
                required
              >
                <option value="">Select Semester</option>
                <option value="Semester 1">Semester 1</option>
                <option value="Semester 2">Semester 2</option>
              </select>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="examType">Exam Type</label>
              <select
                id="examType"
                name="examType"
                value={evaluationDetails.examType}
                onChange={handleDetailsChange}
                className="evaluation-input"
                disabled={!selectedCourseKey || availableExamTypes.length === 0}
                required
              >
                <option value="">
                  {selectedCourseKey ? 'Select Exam Type' : 'Select a course first'}
                </option>
                {availableExamTypes.map((option) => (
                  <option key={option.value} value={option.value} title={option.description}>
                    {option.value}
                  </option>
                ))}
              </select>
          
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="examFormat">Exam Format</label>
              <select
                id="examFormat"
                name="examFormat"
                value={evaluationDetails.examFormat}
                onChange={handleDetailsChange}
                className="evaluation-input"
                required
              >
                <option value="">Select Exam Format</option>
                <option value="open">Open Book</option>
                <option value="closed">Closed Book</option>
              </select>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="programCommittee">Program Committee</label>
              <select
                id="programCommittee"
                name="programCommittee"
                value={evaluationDetails.programCommittee}
                onChange={handleDetailsChange}
                className="evaluation-input"
                required
              >
                <option value="">Select Program Committee</option>
                <option value="CSIS">CSIS</option>
                <option value="Core Engg.">Core Engg.</option>
                <option value="Management">Management</option>
                <option value="EEE">EEE</option>
              </select>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="batch">Batch</label>
              <input
                id="batch"
                name="batch"
                type="text"
                value={evaluationDetails.batch}
                className="evaluation-input"
                placeholder="Auto-filled after exam type selection"
                readOnly
                required
              />
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="instructorName">Instructor Name</label>
              <input
                id="instructorName"
                name="instructorName"
                type="text"
                value={evaluationDetails.instructorName || ''}
                className="evaluation-input"
                placeholder="Faculty email ID from selected course"
                readOnly
              />
       
            </div>
{/* 
            <div className="evaluation-form-group">
              <label htmlFor="evaluationType">Evaluation Type</label>
              <input
                id="evaluationType"
                name="evaluationType"
                type="text"
                value={evaluationDetails.evaluationType}
                className="evaluation-input"
                placeholder="Optional; auto-filled when available"
                readOnly
              />
            </div> */}

          </div>
        </div>

        {searchError && (
          <div className="error-alert">{searchError}</div>
        )}

        {/* Deliberately no results table here. Review Papers is the single
            place reviews live now, for both this pipeline and the upload/zip
            one, so scores and ratings are not duplicated on this page. A fetch
            reports how many papers it matched, offers a way through to read
            them, and lists the slots it has not covered. */}
        {searchResults && (
          <div className="automated-evaluation-card">
            <div className="evaluation-details-header">
              <h2>Results</h2>
              <p>
                {Object.keys(searchResults.filters_applied || {}).length === 0
                  ? 'Matched the whole catalogue (no filters applied).'
                  : `Matched for ${Object.entries(searchResults.filters_applied).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(', ')}.`}
              </p>
            </div>

            {/* Counts only. Scores and ratings live in Review Papers, which is
                the single place reviews are read for both pipelines. */}
            <div className="ae-count-row">
              <div className="ae-count">
                <span className="ae-count-value">{searchResults.evaluated_count}</span>
                <span className="ae-count-label">evaluated</span>
              </div>
              <div className="ae-count">
                <span className="ae-count-value">
                  {searchResults.pending && searchResults.pending.available
                    ? searchResults.pending.count
                    : '—'}
                </span>
                <span className="ae-count-label">pending</span>
              </div>
            </div>

            {/* Outcomes of "Evaluate now", rendered here rather than on the
                pending row itself. A finished slot stops being pending, so the
                refresh that follows success unmounts its row - reporting the
                result inside it meant the row silently vanished and the user was
                never told the evaluation had worked. slotState is keyed by slot,
                so it survives the refresh. */}
            {Object.entries(slotState)
              .filter(([, s]) => s.phase === 'done' || s.phase === 'error')
              .map(([key, s]) => (
                <div
                  key={key}
                  className={s.phase === 'done' ? 'ae-slot-result-ok' : 'ae-slot-result-bad'}
                >
                  <FontAwesomeIcon icon={s.phase === 'done' ? faCheckCircle : faBolt} />{' '}
                  <strong>{key.split('|')[0]} {key.split('|')[1]}</strong>{' '}
                  {s.phase === 'done'
                    ? `evaluated${s.questions ? ` · ${s.questions} questions extracted` : ''}. It is in Review Papers now.`
                    : s.message}
                </div>
              ))}

            {searchResults.evaluated_count > 0 && (
              <div className="review-papers-pointer">
                <p>
                  {searchResults.evaluated_count} evaluated paper
                  {searchResults.evaluated_count === 1 ? ' is' : 's are'} ready.
                  {/* The browsable list below is capped at `limit` (a stored-paper
                      count in the thousands is ordinary, and fetching every row's
                      full detail just to show a tally would not be) - so once the
                      true total exceeds what was actually fetched, say so rather
                      than let someone assume the list below is everything. */}
                  {searchResults.truncated && (
                    ` Showing the most recent ${searchResults.results.length} below - `
                    + `narrow the filters (course code, exam type) to find a specific one.`
                  )}
                </p>
              </div>
            )}
            {searchResults.evaluated_count === 0 && (
              <p>No evaluated papers match these filters yet.</p>
            )}

            {/* The evaluated paper itself. It is already stored, so this is read
                out of storage rather than fetched from upstream - no processing,
                nothing to wait for beyond the download. */}
            {evaluatedResults.length > 0 && (
              <div className="ae-preview">
                <div className="ae-preview-head">
                  <h3>
                    <FontAwesomeIcon icon={faFilePdf} /> Paper preview
                  </h3>
                  {evaluatedResults.length > 1 && (
                    <span className="ae-preview-count">
                      {visiblePreviewOptions.length === evaluatedResults.length
                        ? `${evaluatedResults.length} papers`
                        : `${visiblePreviewOptions.length} of ${evaluatedResults.length} papers`}
                    </span>
                  )}
                </div>
                {evaluatedResults.length > 1 && (
                  <>
                    <p className="ae-preview-note">
                      Pick one to preview it here. Picking swaps the preview and changes
                      nothing else.
                    </p>
                    {/* A filter box and a scroll container, rather than a native
                        <select>. With a hundred-odd matches the dropdown was a
                        list you scrolled blind: no way to narrow it, and it grew
                        with the catalogue. */}
                    <div className="ae-picker">
                      <input
                        type="text"
                        className="ae-picker-search"
                        value={previewQuery}
                        onChange={(event) => setPreviewQuery(event.target.value)}
                        placeholder="Filter by course code or name"
                        aria-label="Filter the matched papers"
                      />
                      <div className="ae-picker-list" role="listbox">
                        {visiblePreviewOptions.length === 0 ? (
                          <p className="ae-picker-empty">No matched paper contains that.</p>
                        ) : (
                          visiblePreviewOptions.map((paper) => (
                            <button
                              type="button"
                              key={paper.paper_id}
                              role="option"
                              aria-selected={paper.paper_id === previewPaperId}
                              className={'ae-picker-option'
                                + (paper.paper_id === previewPaperId ? ' is-active' : '')}
                              onClick={() => loadPaperPreview(paper.paper_id)}
                            >
                              <span className="ae-picker-code">{paper.course_id}</span>
                              <span className="ae-picker-name">{paper.course_name || ''}</span>
                              <span className="ae-picker-meta">
                                {paper.exam_type} · {paper.academic_year} SEM {paper.semester}
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
                {paperPreview.loading && (
                  <p className="ae-preview-status">
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading the paper...
                  </p>
                )}
                {paperPreview.error && (
                  <p className="ae-preview-status ae-preview-status-bad">{paperPreview.error}</p>
                )}
                {paperPreview.url && (
                  <div className="ae-preview-frame">
                    <object data={paperPreview.url} type="application/pdf">
                      <p>
                        This browser cannot display the PDF inline.{' '}
                        <a href={paperPreview.url} target="_blank" rel="noreferrer">
                          Open it in a new tab
                        </a>
                        .
                      </p>
                    </object>
                  </div>
                )}
              </div>
            )}

            {searchResults.pending && !searchResults.pending.available && (
              <div className="ae-notice">
                Pending count unavailable ({searchResults.pending.reason}). The evaluated
                count above is still accurate.
              </div>
            )}

            {/* Pending slots, each with its own trigger. The upstream catalogue
                is the source here, not question_papers - these are slots the
                background job has not reached, so there is no paper yet. */}
            {searchResults.pending && searchResults.pending.available
              && searchResults.pending.count > 0 && (
              <div className="ae-pending">
                <div className="ae-pending-head">
                  <h3>Not evaluated yet</h3>
                  <button
                    type="button"
                    className="btn-queue-all"
                    onClick={handleQueueAllPending}
                    disabled={isQueueing || !searchResults.pending.count}
                    title={`Queue all ${searchResults.pending.count} pending paper(s) for background evaluation. The server works them in batches; this page does not need to stay open.`}
                  >
                    {isQueueing
                      ? 'Queueing...'
                      : `Evaluate all pending (${searchResults.pending.count})`}
                  </button>
                </div>

                {bulkQueue && (
                  <div className={bulkQueue.error ? 'error-alert' : 'ae-queued-note'}>
                    {bulkQueue.error ? bulkQueue.error : (
                      <>
                        <strong>{bulkQueue.queued} paper(s) queued</strong>
                        {bulkQueue.batches > 1
                          && ` in ${bulkQueue.batches} batches of up to ${bulkQueue.batch_size}`}.
                        {' '}They evaluate in the background one batch at a time, so you can
                        close this page - press <strong>Fetch Question Papers</strong> later to
                        see the counts move.
                        {bulkQueue.truncated && (
                          <>
                            {' '}Only the first {bulkQueue.queued} of {bulkQueue.pending_total} were
                            taken, because a limit was asked for.
                          </>
                        )}
                        {bulkQueue.queue_group_id && !bulkQueue.cancelled && (
                          <div className="ae-queued-actions">
                            <button
                              type="button"
                              className="btn-cancel-queue"
                              onClick={handleCancelQueue}
                              disabled={isCancelling}
                              title="Stop the batches that have not started yet. A paper already being evaluated finishes."
                            >
                              {isCancelling ? 'Stopping...' : 'Stop remaining batches'}
                            </button>
                          </div>
                        )}
                        {bulkQueue.cancelError && (
                          <div className="ae-queued-cancelled">{bulkQueue.cancelError}</div>
                        )}
                        {bulkQueue.cancelled && (
                          <div className="ae-queued-cancelled">
                            <strong>Stopped.</strong> {bulkQueue.cancelled.message}
                            {' '}Nothing was lost - the papers that did not run are still
                            pending, so they will be picked up by a later queue or by the
                            background job.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <p className="ae-pending-note">
                  {searchResults.pending.upstream_matches} upstream paper(s) match these
                  filters and {searchResults.pending.already_stored} have been evaluated.
                  The background job will reach the rest on its own; "Evaluate now" pulls
                  a single one forward and <strong>Preview</strong> just shows the paper.
                  {searchResults.pending.count > (searchResults.pending.sample || []).length && (
                    <>
                      {' '}Showing {pendingOffset + 1}-
                      {pendingOffset + (searchResults.pending.sample || []).length} of{' '}
                      {searchResults.pending.count}; page through them below.
                    </>
                  )}
                </p>

                {(searchResults.pending.sample || []).map((slot) => {
                  const key = slotKey(slot);
                  const state = slotState[key] || {};
                  const isPreviewing = slotPreview.key === key
                    && (slotPreview.url || slotPreview.loading || slotPreview.error);
                  return (
                    <React.Fragment key={key}>
                    <div className={"ae-pending-row" + (isPreviewing ? " is-previewing" : "")}>
                      <div className="ae-pending-meta">
                        <strong>{slot.course_id}</strong>
                        <span>{slot.course_name}</span>
                        <span className="ae-pending-sub">
                          {slot.exam_type} · {slot.academic_year} SEM {slot.semester} · {slot.batch}
                        </span>
                      </div>
                      <div className="ae-pending-action">
                        {/* Read-only. Separate from "Evaluate now" on purpose:
                            it shows the paper without creating one, scoring it,
                            or claiming the slot. */}
                        <button
                          type="button"
                          className="btn-preview-slot"
                          onClick={() => loadSlotPreview(slot)}
                          disabled={slotPreview.loading && slotPreview.key === key}
                          title="Fetch just this paper from QPM and show it. Nothing is stored and no scoring is started."
                        >
                          {slotPreview.loading && slotPreview.key === key ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faEye} /> Preview
                            </>
                          )}
                        </button>
                        {state.phase === 'done' ? (
                          <span className="ae-pending-done">
                            <FontAwesomeIcon icon={faCheckCircle} /> evaluated
                            {state.questions ? ` · ${state.questions} questions` : ''}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn-evaluate-now"
                            onClick={() => handleEvaluateSlot(slot)}
                            disabled={state.phase === 'starting' || state.phase === 'running'}
                            title="Fetch, partition and score this one paper now"
                          >
                            {state.phase === 'starting' || state.phase === 'running' ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin />{' '}
                                {state.phase === 'starting' ? 'Starting...' : 'Evaluating...'}
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faBolt} /> Evaluate now
                              </>
                            )}
                          </button>
                        )}
                        {state.phase === 'error' && (
                          <span className="ae-pending-error">{state.message}</span>
                        )}
                      </div>
                    </div>
                    {isPreviewing && renderSlotPreview()}
                    </React.Fragment>
                  );
                })}

                {/* Pager. Previously this list stopped at the first ten and the
                    only way to reach the rest was to narrow the filters, which
                    is not a way to browse candidates. */}
                {(pendingOffset > 0 || searchResults.pending.has_more) && (
                  <div className="ae-pending-pager">
                    <button
                      type="button"
                      className="btn-pending-page"
                      onClick={() => handleSearchEvaluated(Math.max(0, pendingOffset - PENDING_PAGE_SIZE))}
                      disabled={pendingOffset === 0 || isSearching}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} /> Previous
                    </button>
                    <span className="ae-pending-pager-label">
                      {pendingOffset + 1}-{pendingOffset + (searchResults.pending.sample || []).length}
                      {' of '}
                      {searchResults.pending.count}
                    </span>
                    <button
                      type="button"
                      className="btn-pending-page"
                      onClick={() => handleSearchEvaluated(pendingOffset + PENDING_PAGE_SIZE)}
                      disabled={!searchResults.pending.has_more || isSearching}
                    >
                      Next <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* One action. The live fetch-and-score buttons that used to sit here
            ("Upload Question Paper", and the live QPM fetch that fed it) are
            gone: with the background job pre-evaluating the catalogue, kicking
            off a live fetch-and-score for a whole form from this page is not a
            sensible general action. What remains is the lookup against
            already-evaluated results, and - per pending row below - a narrow
            per-paper trigger for the specific slots the job has not reached. */}
        <div className="evaluation-actions">
          <button
            type="button"
            className="btn-fetch-papers"
            onClick={() => handleSearchEvaluated(0)}
            disabled={isSearching}
            title="Show every already-evaluated paper matching the fields you have filled in. No field is required."
          >
            {isSearching ? 'Searching...' : 'Fetch Question Papers'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomatedEvaluation;
